import type { FC, ReactNode } from 'react'

import Lenis from 'lenis'
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState
} from 'react'
import { gsap, ScrollTrigger } from '@/lib/motion'

interface SmoothScrollProps {
    children: ReactNode
}

const LenisContext = createContext<Lenis | null>(null)

export const useLenis = (): Lenis | null => useContext(LenisContext)

const documentTop = (element: HTMLElement): number => element.getBoundingClientRect().top + window.scrollY

const scrollMargin = (element: HTMLElement): number => Number.parseFloat(window.getComputedStyle(element).scrollMarginTop) || 0

// Scroll position of a nav target. Every target lands with its heading below the fixed header and
// the YouTube card, so nothing a jump reveals is covered:
// - inside a pinned scene ([data-scroll-track]) desktop goes to the scene's own progress point
//   ([data-scroll-progress]), where that chapter is laid out below the card;
// - a section with [data-scroll-skip] on desktop starts that many screen heights in (MUSIC skips
//   its star intro so the songs are already showing);
// - otherwise the section's [data-scroll-anchor] child (or the element itself) lands at its CSS
//   scroll-margin-top. An anchor inside a sticky stage ([data-scroll-stage], always the first
//   child at the very top of its section) is measured from where the stage rests, the section
//   top, so the result is the same whether the page is above or below the section.
export const getScrollTarget = (id: string): number | null => {
    if (id === 'top') return 0

    const element = document.getElementById(id)

    if (!element) return null

    const desktop = window.matchMedia('(min-width: 1024px)').matches
    const track = element.closest<HTMLElement>('[data-scroll-track]')
    const progress = Number.parseFloat(element.dataset.scrollProgress ?? '')

    if (track && !Number.isNaN(progress) && desktop) {
        return Math.max(0, documentTop(track) + (track.offsetHeight - window.innerHeight) * progress)
    }

    const skip = Number.parseFloat(element.dataset.scrollSkip ?? '')

    if (desktop && !Number.isNaN(skip) && element.hasAttribute('data-enhanced')) {
        return Math.max(0, documentTop(element) + window.innerHeight * skip)
    }

    const anchor = element.querySelector<HTMLElement>('[data-scroll-anchor]') ?? element
    const stage = anchor === element ? null : anchor.closest<HTMLElement>('[data-scroll-stage]')

    if (stage && element.contains(stage)) {
        const offset = anchor.getBoundingClientRect().top - stage.getBoundingClientRect().top

        return Math.max(0, documentTop(element) + offset - scrollMargin(anchor))
    }

    return Math.max(0, documentTop(anchor) - scrollMargin(anchor))
}

export const useScrollTo = (): ((id: string) => void) => {
    const lenis = useLenis()

    return useCallback((id: string) => {
        const target = getScrollTarget(id)

        if (target === null) return

        if (lenis) {
            lenis.scrollTo(target, { duration: 1.9, easing: (t: number) => 1 - Math.pow(1 - t, 4) })

            return
        }

        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches

        window.scrollTo({ top: target, behavior: reduce ? 'auto' : 'smooth' })
    }, [lenis])
}

const SmoothScroll: FC<SmoothScrollProps> = ({ children }): ReactNode => {
    const [lenis, setLenis] = useState<Lenis | null>(null)

    useEffect(() => {
        // The opening sequence always starts from the top of the page.
        if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual'

        window.scrollTo(0, 0)

        let instance: Lenis | null = null
        let tick: ((time: number) => void) | null = null

        if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            instance = new Lenis({
                lerp: 0.085,
                wheelMultiplier: 0.9,
                smoothWheel: true
            })

            instance.on('scroll', ScrollTrigger.update)

            const smooth = instance

            tick = (time: number) => smooth.raf(time * 1000)

            gsap.ticker.add(tick)
            gsap.ticker.lagSmoothing(0)

            setLenis(instance)
        }

        let active = true

        // Arriving from another page with an address such as /#music: go to that section once the
        // layout is measured, and correct the position again after the web fonts arrive (unless
        // the visitor has scrolled away in the meantime).
        const hashId = decodeURIComponent(window.location.hash.slice(1))
        let landedAt: number | null = null

        const jumpToHash = (): void => {
            if (!hashId || !active) return
            if (landedAt !== null && Math.abs(window.scrollY - landedAt) > 4) return

            const target = getScrollTarget(hashId)

            if (target === null) return

            landedAt = target

            if (instance) instance.scrollTo(target, { immediate: true, force: true })
            else window.scrollTo(0, target)
        }

        // Sections switch to their pinned desktop layouts while mounting, so measure everything again
        // once the page has settled, and again when the web fonts have arrived.
        const frame = window.requestAnimationFrame(() => {
            if (!active) return

            ScrollTrigger.refresh()
            jumpToHash()
        })

        document.fonts?.ready
            .then(() => {
                if (!active) return

                ScrollTrigger.refresh()
                jumpToHash()
            })
            .catch(() => undefined)

        return () => {
            active = false
            window.cancelAnimationFrame(frame)

            if (tick) gsap.ticker.remove(tick)

            instance?.destroy()
            setLenis(null)
        }
    }, [])

    return (
        <LenisContext.Provider value={lenis}>
            {children}
        </LenisContext.Provider>
    )
}

export default SmoothScroll
